import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Dimmer, Icon, Loader, Message } from 'semantic-ui-react';
import { SemanticToastContainer } from 'react-semantic-toasts';
import { AddButton } from '../../components/AddButton/AddButton';
import { useAuth } from '../../providers/AuthenticationProvider';
import { useIssueOptions } from '../../providers/IssueOptionsProvider';
import { IssueOptionDeleteConfirmation } from './IssueOptionDeleteConfirmation';
import { IssueOptionModal } from './IssueOptionModal';
import { IssueOptionTabs } from './IssueOptionTabs';
import { getErrorMessage, notifyError, notifySuccess } from './issueOptionToasts';
import './IssueOptions.css';

const WEB_PAGE = 'Issue Options';

const TAB_ORDER = ['priorities', 'statuses', 'types'];

const TAB_CONFIG = {
    priorities: { singular: 'Priority', plural: 'Priorities' },
    statuses: { singular: 'Status', plural: 'Statuses' },
    types: { singular: 'Type', plural: 'Types' },
};

export const IssueOptions = () => {
    const { checkAuth, getUserType } = useAuth();
    const navigate = useNavigate();

    checkAuth(getUserType(), WEB_PAGE) && navigate('/');

    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const activeKind = TAB_ORDER.includes(tabParam) ? tabParam : 'priorities';
    const config = TAB_CONFIG[activeKind];

    const { issueOptions, getOptions, createOption, updateOption, deleteOption, getUsage } = useIssueOptions();
    const { items, loading, error } = issueOptions[activeKind];

    const [modalOpen, setModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [usage, setUsage] = useState(null);
    const [usageError, setUsageError] = useState(false);
    const [usageLoading, setUsageLoading] = useState(false);
    const [preparingId, setPreparingId] = useState(null);
    const submitLock = useRef(false);
    const usageRequestId = useRef(0);

    useEffect(() => {
        if (tabParam !== activeKind) {
            setSearchParams(
                prev => {
                    prev.set('tab', activeKind);
                    return prev;
                },
                { replace: true }
            );
        }
    }, [tabParam, activeKind, setSearchParams]);

    useEffect(() => {
        getOptions(activeKind);
    }, [activeKind, getOptions]);

    useEffect(() => {
        setModalOpen(false);
        setEditingItem(null);
        usageRequestId.current++;
        setDeleteTarget(null);
        setUsage(null);
        setUsageError(false);
        setUsageLoading(false);
        setPreparingId(null);
    }, [activeKind]);

    const selectTab = kind =>
        setSearchParams(
            prev => {
                prev.set('tab', kind);
                return prev;
            },
            { replace: true }
        );

    const openAdd = () => {
        setEditingItem(null);
        setModalOpen(true);
    };

    const openRename = item => {
        setEditingItem(item);
        setModalOpen(true);
    };

    const fetchUsage = async id => {
        const requestId = ++usageRequestId.current;
        setUsage(null);
        setUsageError(false);
        setUsageLoading(true);
        try {
            const result = await getUsage(activeKind, id);
            if (usageRequestId.current !== requestId) return;
            setUsage(result ?? null);
        } catch (err) {
            if (usageRequestId.current !== requestId) return;
            setUsageError(true);
        } finally {
            if (usageRequestId.current === requestId) setUsageLoading(false);
        }
    };

    const openDelete = async item => {
        const requestId = ++usageRequestId.current;
        setPreparingId(item.id);
        setUsage(null);
        setUsageError(false);
        setUsageLoading(false);
        try {
            const result = await getUsage(activeKind, item.id);
            if (usageRequestId.current !== requestId) return;
            setUsage(result ?? null);
            setDeleteTarget(item);
        } catch (err) {
            if (usageRequestId.current !== requestId) return;
            if (err?.response?.status === 404) {
                getOptions(activeKind);
                notifyError(err, config.singular);
                return;
            }
            setUsageError(true);
            setDeleteTarget(item);
        } finally {
            if (usageRequestId.current === requestId) setPreparingId(null);
        }
    };

    const handleSubmitName = async name => {
        if (submitLock.current) return;
        submitLock.current = true;
        setSubmitting(true);
        try {
            if (editingItem) {
                await updateOption(activeKind, editingItem.id, name);
                notifySuccess(config.singular, 'updated');
            } else {
                await createOption(activeKind, name);
                notifySuccess(config.singular, 'created');
            }
            setModalOpen(false);
        } catch (err) {
            if (err?.response?.status === 404) {
                setModalOpen(false);
                getOptions(activeKind);
                notifyError(err, config.singular);
                return;
            }
            throw new Error(getErrorMessage(err, config.singular));
        } finally {
            setSubmitting(false);
            submitLock.current = false;
        }
    };

    const confirmDelete = async () => {
        if (submitLock.current) return;
        submitLock.current = true;
        setSubmitting(true);
        try {
            await deleteOption(activeKind, deleteTarget.id);
            notifySuccess(config.singular, 'deleted');
            setDeleteTarget(null);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 404) {
                setDeleteTarget(null);
                getOptions(activeKind);
                notifyError(err, config.singular);
                return;
            }
            if (status === 409) {
                fetchUsage(deleteTarget.id);
                return;
            }
            notifyError(err, config.singular);
        } finally {
            setSubmitting(false);
            submitLock.current = false;
        }
    };

    const renderBody = () => {
        if (loading) {
            return (
                <div className="issue-options-state" style={{ position: 'relative', minHeight: '8rem' }}>
                    <Dimmer active inverted>
                        <Loader>Loading...</Loader>
                    </Dimmer>
                </div>
            );
        }

        if (error) {
            return (
                <div className="issue-options-state" role="alert">
                    <Message negative>
                        <Message.Header>Unable to load {config.plural.toLowerCase()}</Message.Header>
                        <p>Something went wrong while loading this list.</p>
                    </Message>
                    <Button
                        primary
                        icon
                        labelPosition="left"
                        className="issue-options-retry"
                        onClick={() => getOptions(activeKind)}
                    >
                        <Icon name="redo" />
                        Retry
                    </Button>
                </div>
            );
        }

        if (!items.length) {
            return (
                <div className="issue-options-empty">
                    No {config.plural.toLowerCase()} yet.
                </div>
            );
        }

        return (
            <ul className="issue-options-list">
                {items.map(item => (
                    <li className="issue-options-row" key={item.id}>
                        <span className="issue-options-row-name">{item.name}</span>
                        <div className="issue-options-row-actions">
                            <Button
                                size="small"
                                onClick={() => openRename(item)}
                                disabled={submitting || preparingId !== null}
                            >
                                Rename
                            </Button>
                            <Button
                                size="small"
                                onClick={() => openDelete(item)}
                                loading={preparingId === item.id}
                                disabled={submitting || preparingId !== null}
                            >
                                Delete
                            </Button>
                        </div>
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <div className="issue-options-page">
            <IssueOptionModal
                open={modalOpen}
                mode={editingItem ? 'rename' : 'add'}
                singular={config.singular}
                initialName={editingItem?.name}
                submitting={submitting}
                onSubmit={handleSubmitName}
                onCancel={() => setModalOpen(false)}
            />
            <IssueOptionDeleteConfirmation
                open={!!deleteTarget}
                setOpen={open => {
                    if (!open) {
                        usageRequestId.current++;
                        setDeleteTarget(null);
                        setUsage(null);
                        setUsageError(false);
                        setUsageLoading(false);
                        setPreparingId(null);
                    }
                }}
                item={deleteTarget}
                singular={config.singular}
                usage={usage}
                usageError={usageError}
                usageLoading={usageLoading}
                deleting={submitting}
                onConfirm={confirmDelete}
            />

            <div className="pageHeader">
                <h1>Issue Options</h1>
            </div>

            <div className="issue-options-toolbar">
                <IssueOptionTabs
                    tabs={TAB_ORDER.map(kind => ({ kind, label: TAB_CONFIG[kind].plural }))}
                    activeKind={activeKind}
                    onSelect={selectTab}
                    disabled={submitting}
                />
                <AddButton onClick={openAdd} disabled={submitting || loading || error}>
                    Add {config.singular}
                </AddButton>
            </div>

            <div
                id={`issue-options-panel-${activeKind}`}
                role="tabpanel"
                aria-labelledby="issue-options-selector"
                aria-busy={loading}
                tabIndex={0}
                className="issue-options-panel"
            >
                {renderBody()}
            </div>

            <SemanticToastContainer position="bottom-center" />
        </div>
    );
};
